/**
 * Task-quota billing window.
 *
 * Matches scaffold-main Menu.tsx: even yearly *payment* plans reset task usage
 * monthly on `anchorDate`. `nextBillingDate` is used for monthly plans.
 * nextBillingDate is Unix seconds on the tenant document.
 */

const toYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const clampAnchorDay = (year, monthIndex, anchorDay) => {
  const day = Math.min(Math.max(1, Number(anchorDay) || 1), 31);
  // Day 31 in April → rolls to May 1 in JS; clamp to last day of month instead.
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return Math.min(day, lastDay);
};

/** Next monthly reset at local midnight on the anchor day (scaffold yearly logic). */
const nextMonthlyAnchorReset = (now, anchorDay) => {
  const y = now.getFullYear();
  const m = now.getMonth();
  const dayThisMonth = clampAnchorDay(y, m, anchorDay);
  // On/after the anchor day, the current period already started — next reset is next month.
  if (now.getDate() >= dayThisMonth) {
    const nextM = m + 1;
    const ny = nextM > 11 ? y + 1 : y;
    const nm = nextM % 12;
    return new Date(ny, nm, clampAnchorDay(ny, nm, anchorDay), 0, 0, 0, 0);
  }
  return new Date(y, m, dayThisMonth, 0, 0, 0, 0);
};

const previousMonthlyAnchorStart = (nextReset, anchorDay) => {
  const y = nextReset.getFullYear();
  const m = nextReset.getMonth() - 1;
  const py = m < 0 ? y - 1 : y;
  const pm = (m + 12) % 12;
  return new Date(py, pm, clampAnchorDay(py, pm, anchorDay), 0, 0, 0, 0);
};

const calendarMonthPeriod = (now = new Date()) => {
  const periodStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const nextReset = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);
  const periodEnd = new Date(nextReset.getTime() - 1);
  const daysUntilReset = Math.max(
    0,
    Math.ceil((nextReset.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)),
  );
  return { periodStart, periodEnd, nextReset, daysUntilReset, source: "calendar_month" };
};

/**
 * Resolve the *task quota* window (always ~1 month).
 * Yearly planType still uses monthly anchor resets — same as the dashboard meter.
 */
export const resolveBillingPeriod = (tenant = null, now = new Date()) => {
  const planType = String(tenant?.planType || "").toLowerCase();
  const anchor = Number(tenant?.anchorDate);
  const nextUnix = Number(tenant?.nextBillingDate);
  const nowMs = now.getTime();

  let nextReset = null;
  let source = "tenant";

  // Yearly (and any tenant with anchorDate): monthly quota on anchor day — matches scaffold.
  if (Number.isFinite(anchor) && anchor >= 1 && anchor <= 31) {
    nextReset = nextMonthlyAnchorReset(now, anchor);
    source = planType === "yearly" ? "yearly_monthly_anchor" : "anchor";
  } else if (Number.isFinite(nextUnix) && nextUnix > 1000) {
    nextReset = new Date(nextUnix * 1000);
    source = "next_billing_date";
  }

  if (!nextReset || Number.isNaN(nextReset.getTime())) {
    return calendarMonthPeriod(now);
  }

  let periodStart;
  if (Number.isFinite(anchor) && anchor >= 1 && anchor <= 31) {
    periodStart = previousMonthlyAnchorStart(nextReset, anchor);
  } else {
    periodStart = new Date(nextReset.getTime());
    periodStart.setMonth(periodStart.getMonth() - 1);
  }

  // Inclusive end = last ms before next reset (half-open [start, nextReset)).
  const periodEnd = new Date(nextReset.getTime() - 1);
  const daysUntilReset = Math.max(0, Math.ceil((nextReset.getTime() - nowMs) / (24 * 60 * 60 * 1000)));

  return {
    periodStart,
    periodEnd,
    nextReset,
    daysUntilReset,
    source,
  };
};

export const billingPeriodQuery = (tenant) => {
  const { periodStart, periodEnd, nextReset, daysUntilReset, source } = resolveBillingPeriod(tenant);
  return {
    dateFrom: toYmd(periodStart),
    dateTo: toYmd(periodEnd),
    periodStart,
    periodEnd,
    nextReset,
    daysUntilReset,
    source,
  };
};

/** Free-plan task allowance. These credits never reset on a billing month. */
export const FREE_LIFETIME_TASK_QUOTA = 300;

const LIFETIME_FREE_PLANS = new Set(["free", "free2", "free2m"]);

export const isLifetimeTaskQuota = (tenant) =>
  LIFETIME_FREE_PLANS.has(String(tenant?.plan || "").toLowerCase());

const nonNegative = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Credits that count against the plan cap.
 * Free plans use the greater of creditUsage and lifetimeCreditUsage so a monthly
 * creditUsage reset cannot reopen the 300 lifetime allowance.
 */
export const resolveTaskUsage = (tenant) => {
  const credit = nonNegative(tenant?.creditUsage);
  if (!isLifetimeTaskQuota(tenant)) return Number(tenant?.creditUsage) || 0;
  return Math.max(credit, nonNegative(tenant?.lifetimeCreditUsage));
};

/** Same quota display rule as scaffold-main Task Usage meter. */
export const resolvePlanQuota = (tenant) => {
  if (isLifetimeTaskQuota(tenant)) return FREE_LIFETIME_TASK_QUOTA;
  const planType = String(tenant?.planType || "").toLowerCase();
  const raw = Number(tenant?.creditQuota);
  if (planType === "canceled" || !Number.isFinite(raw) || raw <= 0) {
    return 300;
  }
  return raw;
};

/**
 * Parse run created_at for billing-period filters (ISO string, ms number, or Date).
 */
export const parseRunCreatedAtMs = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    // Heuristic: seconds vs milliseconds
    return value < 1e12 ? value * 1000 : value;
  }
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
};

/** Inclusive start, exclusive nextReset (or inclusive periodEnd). */
export const runCreatedAtInPeriod = (run, periodStart, periodEnd) => {
  const t = parseRunCreatedAtMs(run?.created_at);
  if (t == null) return false;
  const start = periodStart instanceof Date ? periodStart.getTime() : Number(periodStart);
  const end = periodEnd instanceof Date ? periodEnd.getTime() : Number(periodEnd);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return true;
  return t >= start && t <= end;
};

export const formatPeriodRange = (periodStart, periodEnd) => {
  try {
    if (!periodStart || !periodEnd) return "";
    const start = periodStart instanceof Date ? periodStart : new Date(periodStart);
    const end = periodEnd instanceof Date ? periodEnd : new Date(periodEnd);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
    const sameYear = start.getFullYear() === end.getFullYear();
    const opts = { month: "short", day: "numeric", year: "numeric" };
    if (sameYear) {
      const a = start.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      const b = end.toLocaleDateString(undefined, opts);
      return `${a} – ${b}`;
    }
    return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
  } catch {
    return "";
  }
};
